import { Component, input } from '@angular/core';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { MarkCalculationMethod } from '@examdojo/models/markscheme-v2';
import { QuestionEditorState } from '@examdojo/models/question-editor';
import { QuestionItemsPreviewComponent } from '@examdojo/question';
import { IonContent, IonFooter, IonHeader } from '@ionic/angular/standalone';
import { QuestionPickerDialogService } from './question-picker-dialog.service';
import { QuestionPickerResult } from './question-picker.model';

@Component({
  selector: 'dojo-question-picker-dialog',
  template: `
    <ion-header [translucent]="true" class="text-center shadow-none">
      <div class="text-primary my-3 text-lg font-semibold">You've already started editing this question</div>
    </ion-header>

    <ion-content class="h-full">
      <div class="flex min-h-full w-full">
        <div class="flex w-1/2 flex-col border-r border-neutral-200">
          <div class="mb-2 flex flex-col gap-1 border-b border-neutral-200 bg-amber-50 px-6 py-1 text-amber-700">
            <span class="text-sm font-semibold">Continue Previous Edit</span>
            <span class="text-xs">Resume your progress with all your edits intact.</span>
          </div>
          <div class="px-4">
            <dojo-question-items-preview
              [markCalculationMethod]="markCalculationMethod()"
              [questionItems]="cachedQuestionState().questionItems"
              [imageCache]="cachedQuestionState().imageCache"
            />
          </div>
        </div>
        <div class="flex w-1/2 flex-col">
          <div class="mb-2 flex flex-col gap-1 border-b border-neutral-200 bg-sky-50 px-6 py-1 text-sky-700">
            <span class="text-sm font-semibold">Start Over</span>
            <span class="text-xs">Restart with the original question and no changes.</span>
          </div>
          <div class="px-4">
            <dojo-question-items-preview
              [markCalculationMethod]="markCalculationMethod()"
              [questionItems]="newQuestionState().questionItems"
              [imageCache]="newQuestionState().imageCache"
            />
          </div>
        </div>
      </div>
    </ion-content>

    <ion-footer class="border-t border-neutral-200 shadow-none">
      <div class="flex w-full items-center">
        <div class="flex w-1/2 justify-end border-r border-neutral-200 px-4 py-4">
          <dojo-button (click)="useCached()" class="w-fit" [customColor]="'amber'">
            <dojo-icon [icon]="'arrow-right'" class="mr-2" />
            <span>Choose this version</span></dojo-button
          >
        </div>
        <div class="flex w-1/2 justify-end px-4 py-4">
          <dojo-button (click)="useFresh()" class="w-fit" [customColor]="'sky'">
            <dojo-icon [icon]="'arrows-rotate'" class="mr-2" />
            <span>Start Over</span>
          </dojo-button>
        </div>
      </div>
    </ion-footer>
  `,
  imports: [ButtonComponent, IonFooter, IonHeader, IonContent, QuestionItemsPreviewComponent, IconComponent],
  host: {
    class: 'h-full flex flex-col',
  },
})
export class QuestionPickerDialogComponent {
  constructor(private readonly questionPickerDialogService: QuestionPickerDialogService) {}

  readonly cachedQuestionState = input.required<QuestionEditorState>();
  readonly newQuestionState = input.required<QuestionEditorState>();
  readonly markCalculationMethod = input.required<MarkCalculationMethod>();

  useCached() {
    this.questionPickerDialogService.dismiss(QuestionPickerResult.UseCached);
  }

  useFresh() {
    this.questionPickerDialogService.dismiss(QuestionPickerResult.UseFresh);
  }
}
